import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LayoutTemplate, Plus, Trash2, Save } from "lucide-react";
import { Field, inp } from "@/components/admin";
import ImageUploader from "@/components/admin/ImageUploader";

export function CoursePageBuilderDialog({ courses, onDone }) {
  const [open, setOpen] = useState(false);
  const [courseId, setCourseId] = useState("");
  const empty = {
    hero_subtitle: "", hero_badge_text: "",
    banner_text: "", banner_cta_text: "", banner_cta_url: "",
    faqs: [], mentor_blocks: [], custom_sections: [], testimonials: [],
  };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async (cid) => {
    if (!cid) { setF(empty); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/course-page/${cid}`);
      setF({
        hero_subtitle: data.hero_subtitle || "",
        hero_badge_text: data.hero_badge_text || "",
        banner_text: data.banner_text || "",
        banner_cta_text: data.banner_cta_text || "",
        banner_cta_url: data.banner_cta_url || "",
        faqs: data.faqs || [],
        mentor_blocks: data.mentor_blocks || [],
        custom_sections: data.custom_sections || [],
        testimonials: data.testimonials || [],
      });
    } catch { /* empty state ok */ }
    finally { setBusy(false); }
  };

  useEffect(() => { load(courseId); /* eslint-disable-next-line */ }, [courseId]);

  const setField = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setArrItem = (key, idx, val) => setF((p) => ({ ...p, [key]: p[key].map((x, i) => i === idx ? val : x) }));
  const addArr = (key, val) => setF((p) => ({ ...p, [key]: [...(p[key] || []), val] }));
  const rmArr = (key, idx) => setF((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!courseId) { toast.error("Course select karo"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/course-page/${courseId}`, f);
      toast.success("✅ Course page saved");
      onDone?.();
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-course-page-builder">
          <LayoutTemplate className="w-4 h-4" /> Page Builder
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-fuchsia-300" /> Course Page Customization</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">FAQs, mentor cards, banner, custom sections — per-course customization. CourseDetail page automatically render karega.</div>
        </DialogHeader>

        <Field label="Select course">
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className={inp} data-testid="page-builder-course">
            <option value="">Select course to customize…</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </Field>

        {courseId && (
          <div className="space-y-5 mt-4">
            {/* HERO + BADGE */}
            <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3 space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-fuchsia-300 font-semibold">🎯 Hero customization</div>
              <Field label="Hero subtitle (1-line value prop)">
                <input value={f.hero_subtitle} onChange={(e) => setField("hero_subtitle", e.target.value)} className={inp} placeholder="e.g. India's most practical 3-month digital marketing bootcamp" />
              </Field>
              <Field label="Hero badge text (small tag above title)">
                <input value={f.hero_badge_text} onChange={(e) => setField("hero_badge_text", e.target.value)} className={inp} placeholder="🔥 Limited seats • Next batch starts April 5" />
              </Field>
            </div>

            {/* PROMO BANNER */}
            <div className="rounded-xl border border-amber-400/20 bg-amber-500/[0.04] p-3 space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold">📢 Promo banner (top of course page)</div>
              <Field label="Banner text (leave blank to hide)">
                <input value={f.banner_text} onChange={(e) => setField("banner_text", e.target.value)} className={inp} placeholder="🎁 Get free 1-month mentorship — use code KAMLESH500 at checkout" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="CTA button text"><input value={f.banner_cta_text} onChange={(e) => setField("banner_cta_text", e.target.value)} className={inp} placeholder="Claim now" /></Field>
                <Field label="CTA URL"><input value={f.banner_cta_url} onChange={(e) => setField("banner_cta_url", e.target.value)} className={inp} placeholder="#enroll" /></Field>
              </div>
            </div>

            {/* FAQs */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-semibold">❓ FAQs</div>
                <button type="button" onClick={() => addArr("faqs", { q: "", a: "" })} className="btn-ghost text-xs"><Plus className="w-3 h-3" /> Add FAQ</button>
              </div>
              <div className="space-y-2">
                {f.faqs.map((item, i) => (
                  <div key={i} className="rounded-lg border border-white/10 p-2 space-y-2" data-testid={`faq-${i}`}>
                    <div className="flex gap-2">
                      <input value={item.q} onChange={(e) => setArrItem("faqs", i, { ...item, q: e.target.value })} className={`${inp} flex-1`} placeholder="Question…" />
                      <button onClick={() => rmArr("faqs", i)} className="p-2 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <textarea rows={2} value={item.a} onChange={(e) => setArrItem("faqs", i, { ...item, a: e.target.value })} className={inp} placeholder="Answer…" />
                  </div>
                ))}
                {f.faqs.length === 0 && <div className="text-xs text-white/40 px-3 py-3 border border-dashed border-white/10 rounded-lg text-center">No FAQs yet — add common questions like "kya certificate milega?", "EMI option hai?"</div>}
              </div>
            </div>

            {/* MENTOR BLOCKS */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-semibold">👨‍🏫 Mentor cards</div>
                <button type="button" onClick={() => addArr("mentor_blocks", { name: "", role: "", photo: "", bio: "" })} className="btn-ghost text-xs"><Plus className="w-3 h-3" /> Add mentor</button>
              </div>
              <div className="space-y-2">
                {f.mentor_blocks.map((item, i) => (
                  <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2" data-testid={`mentor-${i}`}>
                    <div className="flex justify-between">
                      <div className="text-xs text-white/50">Mentor #{i + 1}</div>
                      <button onClick={() => rmArr("mentor_blocks", i)} className="p-1 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input value={item.name} onChange={(e) => setArrItem("mentor_blocks", i, { ...item, name: e.target.value })} className={inp} placeholder="Mentor name" />
                      <input value={item.role} onChange={(e) => setArrItem("mentor_blocks", i, { ...item, role: e.target.value })} className={inp} placeholder="Role / specialty (e.g. SEO Lead)" />
                    </div>
                    <ImageUploader value={item.photo} onChange={(url) => setArrItem("mentor_blocks", i, { ...item, photo: url })} label="Photo" aspect="1/1" testId={`mentor-photo-${i}`} />
                    <textarea rows={2} value={item.bio} onChange={(e) => setArrItem("mentor_blocks", i, { ...item, bio: e.target.value })} className={inp} placeholder="Short bio…" />
                  </div>
                ))}
                {f.mentor_blocks.length === 0 && <div className="text-xs text-white/40 px-3 py-3 border border-dashed border-white/10 rounded-lg text-center">Add specialist mentors / guest faculty for this course.</div>}
              </div>
            </div>

            {/* CUSTOM SECTIONS */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-semibold">📑 Custom sections (free-form)</div>
                <button type="button" onClick={() => addArr("custom_sections", { title: "", body: "" })} className="btn-ghost text-xs"><Plus className="w-3 h-3" /> Add section</button>
              </div>
              <div className="space-y-2">
                {f.custom_sections.map((item, i) => (
                  <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2" data-testid={`section-${i}`}>
                    <div className="flex gap-2">
                      <input value={item.title} onChange={(e) => setArrItem("custom_sections", i, { ...item, title: e.target.value })} className={`${inp} flex-1`} placeholder="Section heading (e.g. Placement support)" />
                      <button onClick={() => rmArr("custom_sections", i)} className="p-2 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <textarea rows={3} value={item.body} onChange={(e) => setArrItem("custom_sections", i, { ...item, body: e.target.value })} className={inp} placeholder="Body text — supports paragraph breaks" />
                  </div>
                ))}
                {f.custom_sections.length === 0 && <div className="text-xs text-white/40 px-3 py-3 border border-dashed border-white/10 rounded-lg text-center">Add anything: placement section, learning roadmap, day-wise plan, etc.</div>}
              </div>
            </div>

            {/* TESTIMONIALS */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-semibold">💬 Testimonials</div>
                <button type="button" onClick={() => addArr("testimonials", { name: "", text: "", role: "", photo: "" })} className="btn-ghost text-xs"><Plus className="w-3 h-3" /> Add testimonial</button>
              </div>
              <div className="space-y-2">
                {f.testimonials.map((item, i) => (
                  <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2" data-testid={`testi-${i}`}>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input value={item.name} onChange={(e) => setArrItem("testimonials", i, { ...item, name: e.target.value })} className={inp} placeholder="Student name" />
                      <input value={item.role} onChange={(e) => setArrItem("testimonials", i, { ...item, role: e.target.value })} className={inp} placeholder="Role / company" />
                    </div>
                    <ImageUploader value={item.photo} onChange={(url) => setArrItem("testimonials", i, { ...item, photo: url })} label="Student photo (optional)" aspect="1/1" testId={`testi-photo-${i}`} />
                    <div className="flex gap-2">
                      <textarea rows={2} value={item.text} onChange={(e) => setArrItem("testimonials", i, { ...item, text: e.target.value })} className={`${inp} flex-1`} placeholder="Their quote…" />
                      <button onClick={() => rmArr("testimonials", i)} className="p-2 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SAVE */}
            <div className="sticky bottom-0 bg-[var(--bg-elev-2)] pt-3 -mx-1 px-1 border-t border-white/10">
              <button onClick={save} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="save-course-page">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save course page"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CoursePageBuilderDialog;
